//User Profile
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth, useSnackbar } from "./Contexts";
import { styled } from '@mui/material/styles';
import Avatar from "@mui/material/Avatar";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import {
  isSimplePin,
  SIMPLE_PIN_MESSAGE,
} from "./utils/pinValidation";

const ProfileRoot = styled(Box)({
  width: "100%",
  paddingTop: "10px",
  display: "flex",
  flexDirection: "column",
});

const ProfileInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  border: "2px solid #e63946",
  padding: "5px",
  margin: "auto",
  width: "85%",
  maxWidth: "1200px",
  [theme.breakpoints.down('lg')]: {
    width: "95%",
  },
}));

const StyledAvatar = styled(Avatar)({
  margin: "auto",
  width: "100px",
  height: "100px",
  fontSize: "50px",
  fontWeight: "400",
  backgroundColor: "#e63946",
  color: "#a8dadc",
  marginBottom: "10px",
});

const SaveButton = styled(Button)(({ theme }) => ({
  marginTop: "10px",
  backgroundColor: theme.palette.success.main,
  color: "white",
  "&:hover": {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
    fontWeight: "600",
  },
}));

const UserRolesSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginTop: "5px",
  flexWrap: "wrap",
});

const UserRole = styled(Box)({
  margin: "5px",
  padding: "5px",
  border: "2px solid #dededede",
  borderRadius: "10px",
  backgroundColor: "#1a1a1a1a",
  fontWeight: "500",
});

const UserProfile = () => {
  const Auth = useAuth();
  const snackbar = useSnackbar();
  const LoggedInUserID = Auth.LoggedInUserID;

  async function fetchData(entityName, { filter, fields } = {}) {
    try {
      let finalQuery = {};
      let hasFilter = false;
      if (filter) {
        hasFilter = true;
        finalQuery.where = filter;
      }
      if (fields) {
        hasFilter = true;
        finalQuery.fields = fields;
      }
      var url = `${import.meta.env.VITE_APIURL}${entityName}${
        hasFilter ? `?filter=${JSON.stringify(finalQuery)}` : ""
      }`;
      let data = await Auth.AuthenticatedServerCall(url, "GET");
      return data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  }

  const { data: userInfo, status: userInfoStatus, refetch: userInfoRefetch } = useQuery({
    queryKey: [`users/${LoggedInUserID}`, { filter: { id: LoggedInUserID } }],
    queryFn: ({ queryKey }) => fetchData(queryKey[0], queryKey[1] || {}),
    retry: 2,
    refetchOnWindowFocus: false,
  });
  const { data: userDepartment } = useQuery({
    queryKey: [`user-departments/${userInfo?.userDepartmentId}`],
    queryFn: ({ queryKey }) => fetchData(queryKey[0], queryKey[1] || {}),
    retry: 2,
    refetchOnWindowFocus: false,
    enabled: userInfoStatus === "success" && !!userInfo?.userDepartmentId,
  });

  const USER_ROLES = Auth.LoggedInUserProfile.roles;
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [email, setEmail] = useState("");
  const [isSending, setIsSending] = useState(false);

  const [errors, setErrors] = useState({ pin: "", email: false });

  async function sendToServer() {
    if (!pin || pin.trim().length < 1) {
      setErrors((current) => {
        return { ...current, pin: "Pin is required." };
      });
      return null;
    }
    if (pin.trim() !== userInfo.pin && isSimplePin(pin)) {
      setErrors((current) => ({ ...current, pin: SIMPLE_PIN_MESSAGE }));
      return null;
    }
    if (!email || email.trim().length < 1) {
      setErrors((current) => {
        return { ...current, email: true };
      });
      return null;
    }
    setIsSending(true);
    try {
      let sendingValueToServer = {
        pin: pin.trim(),
        email: email.trim(),
        lastUpdatedBy: LoggedInUserID,
        lastUpdated: new Date(),
      };
      let url = `${import.meta.env.VITE_APIURL}users/${LoggedInUserID}`;
      await Auth.AuthenticatedServerCall(url, "PATCH", sendingValueToServer);
      snackbar.openSnackbar(`Your profile edited successfully`, "success");
      setIsSending(false);
    } catch (e) {
      snackbar.openSnackbar(
        e instanceof Error
          ? e.message
          : "Error occurred when editing your profile!",
        "error"
      );
      setIsSending(false);
      console.log("error", e);
    }
  }

  useEffect(() => {
    if (userInfoStatus === "success" && userInfo) {
      setPin(userInfo.pin);
      setEmail(userInfo.email);
    }
  }, [userInfo, userInfoStatus]);

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <ProfileRoot>
        <StyledAvatar>
          {(() => {
            let name = Auth.LoggedInUserName;
            let initials = name.match(/\b\w/g) || [];
            return (
              (initials.shift() || "") + (initials.pop() || "")
            ).toUpperCase();
          })()}
        </StyledAvatar>
        {userInfoStatus === "success" && userDepartment ? (
          <ProfileInfo>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 5
                }}>
                <TextField
                  fullWidth
                  variant="filled"
                  label="First Name"
                  defaultValue={userInfo.firstName}
                  slotProps={{
                    input: {
                      readOnly: true,
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 5
                }}>
                <TextField
                  fullWidth
                  variant="filled"
                  label="Last Name"
                  defaultValue={userInfo.lastName}
                  slotProps={{
                    input: {
                      readOnly: true,
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <FormControlLabel
                  control={<Checkbox checked={userInfo.isLive} name="isLive" />}
                  label="Is Live"
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  variant="filled"
                  label="User Level"
                  defaultValue={Auth.LoggedInUserProfile.level}
                  slotProps={{
                    input: {
                      readOnly: true,
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  variant="filled"
                  label="User Department"
                  defaultValue={userDepartment.name}
                  slotProps={{
                    input: {
                      readOnly: true,
                    }
                  }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  error={errors.email}
                  fullWidth
                  variant="outlined"
                  label="Email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors((current) => ({ ...current, email: false }));
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 5
                }}>
                <TextField
                  fullWidth
                  variant="filled"
                  label="Clock No"
                  defaultValue={userInfo.clockNo}
                  slotProps={{
                    input: {
                      readOnly: true,
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 5
                }}>
                <FormControl variant="outlined" fullWidth error={!!errors.pin}>
                  <InputLabel htmlFor="pin">Pin</InputLabel>
                  <OutlinedInput
                    id="pin"
                    label="Pin"
                    type={showPin ? "text" : "password"}
                    value={pin}
                    onChange={(e) => {
                      setPin(e.target.value);
                      setErrors((current) => ({ ...current, pin: "" }));
                    }}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle pin visibility"
                          onMouseDown={() => setShowPin(true)}
                          onMouseUp={() => setShowPin(false)}
                          edge="end"
                          size="large">
                          {showPin ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  {errors.pin && <FormHelperText>{errors.pin}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
            {USER_ROLES.length > 0 ? (
              <UserRolesSection>
                <Typography variant="h6">{`Role${
                  USER_ROLES.length > 1 ? "s" : ""
                }: `}</Typography>
                {USER_ROLES.map((role) => (
                  <UserRole key={role}>
                    {role}
                  </UserRole>
                ))}
              </UserRolesSection>
            ) : null}
            {isSending ? (
              <LinearProgress
                color="secondary"
                sx={{ margin: "10px 0px", height: "8px" }}
              />
            ) : (
              <SaveButton
                variant="contained"
                onClick={sendToServer}
              >
                Save
              </SaveButton>
            )}
          </ProfileInfo>
        ) : userInfoStatus === "error" ? (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <h3>Error on loading!</h3>
            <Button
              sx={{ minWidth: "120px", margin: "10px" }}
              variant="contained"
              color="secondary"
              onClick={userInfoRefetch}
            >
              Refetch
            </Button>
          </Box>
        ) : (
          <LinearProgress />
        )}
      </ProfileRoot>
    </Box>
  );
};

export default UserProfile;
